<script>
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import { QuestionTypeEnum, QuizStateEnum } from "@/common/composables/use-vocab-quiz";
import DragDropGame from "@/components/vocabularyTrainer/games/DragDropGame.vue";
import MatchingPairsGame from "@/components/vocabularyTrainer/games/MatchingPairsGame.vue";
import MultipleChoiceGame from "@/components/vocabularyTrainer/games/MultipleChoiceGame.vue";
import TrueFalseGame from "@/components/vocabularyTrainer/games/TrueFalseGame.vue";
import WritingGame from "@/components/vocabularyTrainer/games/WritingGame.vue";
import QuizJumbotron from "@/components/vocabularyTrainer/QuizJumbotron.vue";
import QuizPrompt from "@/components/vocabularyTrainer/QuizPrompt.vue";
import TimeoutProgressBar from "@/components/vocabularyTrainer/TimeoutProgressBar.vue";

export default defineComponent({
  components: {
    DragDropGame,
    MatchingPairsGame,
    MultipleChoiceGame,
    TrueFalseGame,
    WritingGame,
    QuizJumbotron,
    TgButton,
    TimeoutProgressBar,
    QuizPrompt
  },
  props: {
    answerValidator: {
      type: Function,
      required: true
    },
    currentQuestions: {
      type: Array,
      required: true
    },
    currentAnswers: {
      type: Array,
      required: true
    },
    questionType: {
      type: String,
      required: true,
      validator: (value) => Object.values(QuestionTypeEnum).includes(value)
    },
    quizState: {
      type: Number,
      required: true,
      validator: (value) => Object.values(QuizStateEnum).includes(value)
    },
    timeout: {
      type: Number,
      required: true
    }
  },
  emits: ["answer", "skip", "enableQuiz"],
  /**
   *  @typedef {{
   *  answerValidator: Function,
   *  currentQuestions: Array<import("@/common/composables/use-vocab-quiz").Question>,
   *  questionType: import("@/common/composables/use-vocab-quiz").QuestionTypeEnum,
   *  quizState: import("@/common/composables/use-vocab-quiz").QuizStateEnum,
   *  timeout: number
   * }} Props
   *
   * @param {Props} props
   */
  setup(props, { emit }) {
    const { t } = useI18n();

    const questionTypeComponentMap = {
      [QuestionTypeEnum.MultipleChoice]: MultipleChoiceGame,
      [QuestionTypeEnum.TrueFalse]: TrueFalseGame,
      [QuestionTypeEnum.Writing]: WritingGame,
      [QuestionTypeEnum.MatchingPairs]: MatchingPairsGame,
      [QuestionTypeEnum.DragDrop]: DragDropGame
    };
    const currentQuestion = computed(() => props.currentQuestions[0]);
    const questionTypeComponent = questionTypeComponentMap[props.questionType];

    const hasAnswered = ref(false);

    const onAnswer = () => {
      hasAnswered.value = true;
      emit("answer");
    };

    const onEnableQuiz = () => {
      emit("enableQuiz");
    };

    return {
      t,
      emit,

      currentQuestion,
      questionTypeComponent,

      hasAnswered,
      onAnswer,
      onEnableQuiz
    };
  }
});
</script>

<template>
  <QuizJumbotron>
    <div class="container">
      <QuizPrompt
        class="row"
        :question="currentQuestion"
        :question-type="questionType"
        prompt-id="in-quiz-prompt"
        @skip="emit('skip')"
      >
        <template #prompt-image>
          <slot name="quiz-prompt-image" />
        </template>
      </QuizPrompt>
    </div>
    <TimeoutProgressBar
      v-if="hasAnswered"
      class="timeout-progress-bar"
      :height="5"
      :timeout="timeout"
    />
  </QuizJumbotron>

  <div class="quiz">
    <div class="container">
      <Component
        :is="questionTypeComponent"
        class="row"
        prompt-id="in-quiz-prompt"
        :quiz-state="quizState"
        :questions="currentQuestions"
        :user-answers="currentAnswers"
        :answer-validator="answerValidator"
        @answer="onAnswer()"
        @enable-quiz="onEnableQuiz()"
      />
    </div>
    <!-- Mobile only skip button -->
    <div>
      <div class="container d-flex justify-content-center d-lg-none px-3 pt-3">
        <TgButton variant="text" color="grey" size="sm" class="mr-2" @click="emit('skip')">
          {{ t("vocab.games.skip") }}
        </TgButton>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.quiz {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px 0 15px;

  #media-breakpoint-up(lg, {
    padding: 60px 0;
  });

  #media-breakpoint-up(xxl, {
    padding: 80px 0;
  });
}

.timeout-progress-bar {
  margin-bottom: -5px;
}
</style>
